/**
 * FloorPlanEditor - Main visual overlay editor
 *
 * Combines FloorPlanCanvas + EditorToolbar + ShapeDetailPanel
 * Used inside FloorPlanAnalysisWizard as the visual editing step
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import FloorPlanCanvas from './FloorPlanCanvas';
import EditorToolbar from './EditorToolbar';
import ShapeDetailPanel from './ShapeDetailPanel';
import styles from './FloorPlanEditor.module.css';

export default function FloorPlanEditor({
  imageUrl,
  spaces,
  metadata,
  masterSpaces,
  onSpacesChanged,
  onEditorReady: onEditorReadyProp,
}) {
  const [mode, setMode] = useState('select'); // 'select' | 'draw' | 'pan'
  const [selectedShape, setSelectedShape] = useState(null);
  const [allShapes, setAllShapes] = useState([]);
  const editorRef = useRef(null);

  const handleEditorReady = useCallback(
    editor => {
      editorRef.current = editor;
      onEditorReadyProp?.(editor);
    },
    [onEditorReadyProp],
  );

  const getEditor = useCallback(() => editorRef.current, []);

  // Shape selected on canvas
  const handleShapeSelected = useCallback(shape => {
    setSelectedShape(shape);
  }, []);

  // Shape moved/resized on canvas
  const handleShapeModified = useCallback(() => {
    // Force re-render of sidebar
    setAllShapes(prev => [...prev]);
    onSpacesChanged?.();
  }, [onSpacesChanged]);

  // New shape drawn on canvas
  const handleShapeAdded = useCallback(
    shape => {
      setAllShapes(prev => [...prev, shape]);
      setSelectedShape(shape);
      setMode('select');
      onSpacesChanged?.();
    },
    [onSpacesChanged],
  );

  // Update all shapes list when canvas loads
  useEffect(() => {
    // Small delay to let canvas initialize shapes
    const timer = setTimeout(() => {
      const editor = getEditor();
      if (editor) {
        setAllShapes(editor.getShapes());
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [imageUrl, spaces, getEditor]);

  // Mode change
  const handleModeChange = useCallback(newMode => {
    setMode(newMode);
  }, []);

  // Zoom
  const handleZoomIn = useCallback(() => {
    const canvas = getEditor()?.getCanvas();
    if (!canvas) return;
    const zoom = Math.min(canvas.getZoom() * 1.3, 20);
    const center = { x: canvas.width / 2, y: canvas.height / 2 };
    canvas.zoomToPoint(center, zoom);
  }, [getEditor]);

  const handleZoomOut = useCallback(() => {
    const canvas = getEditor()?.getCanvas();
    if (!canvas) return;
    const zoom = Math.max(canvas.getZoom() / 1.3, 0.1);
    const center = { x: canvas.width / 2, y: canvas.height / 2 };
    canvas.zoomToPoint(center, zoom);
  }, [getEditor]);

  const handleFitToScreen = useCallback(() => {
    getEditor()?.fitToScreen();
  }, [getEditor]);

  // Delete selected shape
  const handleDelete = useCallback(() => {
    if (!selectedShape) return;
    getEditor()?.deleteShape(selectedShape);
    setAllShapes(prev => prev.filter(s => s !== selectedShape));
    setSelectedShape(null);
    onSpacesChanged?.();
  }, [selectedShape, getEditor, onSpacesChanged]);

  // Update shape data (from sidebar edits)
  const handleUpdateShape = useCallback(
    shape => {
      // Update label text on canvas
      if (shape.label) {
        const d = shape.spaceData;
        const name = d.suggested_name_he || d.suggested_name || '';
        const size = d.estimated_size_sqm ? `${d.estimated_size_sqm.toFixed(1)} מ"ר` : '';
        shape.label.set({ text: `${name}\n${size}` });
        const canvas = getEditor()?.getCanvas();
        canvas?.requestRenderAll();
      }
      setAllShapes(prev => [...prev]);
      onSpacesChanged?.();
    },
    [getEditor, onSpacesChanged],
  );

  // Select shape from sidebar list
  const handleSelectFromList = useCallback(
    shape => {
      getEditor()?.selectShape(shape);
      setSelectedShape(shape);
    },
    [getEditor],
  );

  // Toggle include/exclude
  const handleToggleInclude = useCallback(
    shape => {
      if (!shape.spaceData) return;
      shape.spaceData.included = !shape.spaceData.included;
      // Update visual opacity
      const canvas = getEditor()?.getCanvas();
      if (canvas) {
        shape.set({ opacity: shape.spaceData.included ? 1 : 0.3 });
        if (shape.label) shape.label.set({ opacity: shape.spaceData.included ? 1 : 0.3 });
        canvas.requestRenderAll();
      }
      setAllShapes(prev => [...prev]);
      onSpacesChanged?.();
    },
    [getEditor, onSpacesChanged],
  );

  // Keyboard handling — intercept Escape and Delete
  useEffect(() => {
    const handleKeyDown = e => {
      // Don't intercept if typing in an input
      if (
        e.target.tagName === 'INPUT' ||
        e.target.tagName === 'SELECT' ||
        e.target.tagName === 'TEXTAREA'
      ) {
        return;
      }

      if (e.key === 'Escape') {
        if (selectedShape) {
          e.stopPropagation(); // Prevent ModalManager from closing
          const canvas = getEditor()?.getCanvas();
          canvas?.discardActiveObject();
          canvas?.requestRenderAll();
          setSelectedShape(null);
        }
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedShape) {
          e.preventDefault();
          handleDelete();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, true); // capture phase
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [selectedShape, getEditor, handleDelete]);

  return (
    <>
      <EditorToolbar
        mode={mode}
        onModeChange={handleModeChange}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onFitToScreen={handleFitToScreen}
        onDelete={handleDelete}
        hasSelection={!!selectedShape}
      />
      <div className={styles.editorBody}>
        <FloorPlanCanvas
          imageUrl={imageUrl}
          spaces={spaces}
          metadata={metadata}
          onShapeSelected={handleShapeSelected}
          onShapeModified={handleShapeModified}
          onShapeAdded={handleShapeAdded}
          onEditorReady={handleEditorReady}
          drawingMode={mode === 'draw'}
          panMode={mode === 'pan'}
        />
        <ShapeDetailPanel
          selectedShape={selectedShape}
          allShapes={allShapes}
          masterSpaces={masterSpaces}
          onUpdateShape={handleUpdateShape}
          onDeleteShape={handleDelete}
          onSelectShape={handleSelectFromList}
          onToggleInclude={handleToggleInclude}
        />
      </div>
    </>
  );
}
